import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import SideNav from "component/layouts/sideNav";
import {
  closureStatusRenderer,
  setItemCategories,
  setItemList,
  setItems,
  setSiteCategories,
  setSites,
  setUoms,
  setUsers,
  siteCellRenderer,
  userCellRenderer,
} from "component/cellRenderers/renderers";
import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";

import {
  ADD,
  CHANGE_STATUS,
  CUSTOMER_MANAGEMENT,
  DELETE,
  GO,
  SALES_ORDERS,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_LOGIN,
  PATH_SALES_ORDERS,
  PATH_SALES_ORDERS_DETAIL_WITHOUT_PARAM,
} from "resources/constant/url/url";

import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APISALES } from "apihandler/sales";
import { useDebounce } from "resources/constant/utilities/util";

import {
  saveGridState,
  saveOrdersToCache,
  setDateRange,
} from "store/Slice/salesOrdersSlice";

export default function SalesOrder() {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const gridRef = useRef();
  const { ordersCache } = useSelector((s) => s.salesOrders);

  const { date } = useParams();

  const soState = useSelector((s) => s.salesOrders);
  const { fromDate, toDate, filterModel, sortModel, page } = soState || {};

  const [dateValue, setDateValue] = useState(null);
  const [toDateValue, setToDateValue] = useState(dayjs().endOf("month"));

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, [navigate]);

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const deleteModel = () => {
    if (selectedRowIds.length === 0) return;

    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      console.log("delete the selected ids: ", selectedRowIds);
      APISALES.DeleteSalesOrders(selectedRowIds)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        });
    });
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      setSelectedRowIds(selectedRows.map((row) => row.id));
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        flex: 1,
        headerName: "Sales Orders No",
        field: "salesOrderNo",
        checkboxSelection: false,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          "border-right-color": "#e2e2e2",
        },
      },
      {
        headerName: "Sales Orders Date",
        field: "salesOrderDate",
        filter: "agTextColumnFilter",
        floatingFilter: true,
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
      },
      {
        flex: 1.2,
        headerName: "Site Name",
        field: "customerId",
        autoHeight: true,
        valueGetter: (params) =>
          siteCellRenderer({ value: params.data.customerId }),
        wrapText: true,
        cellStyle: {
          "border-right-color": "#e2e2e2",
        },
      },
      {
        flex: 0.8,
        headerName: "Amount",
        field: "totalSupplyAmount",
        filter: false,
        floatingFilter: false,
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
      },
      {
        flex: 0.8,
        headerName: "Inst Amount",
        field: "totalInstallationAmount",
        filter: false,
        floatingFilter: false,
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
      },
      {
        flex: 0.8,
        headerName: "Grand Total",
        field: "totalAmount",
        filter: false,
        floatingFilter: false,
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
      },
      {
        flex: 0.8,
        headerName: "Status",
        field: "status",
        filter: "agTextColumnFilter",
        floatingFilter: true,
        valueGetter: (params) =>
          closureStatusRenderer({ value: params.data.status }),
        cellStyle: { "border-right-color": "#e2e2e2" },
      },
      {
        headerName: "Modified By",
        field: "modifiedBy",
        valueGetter: (params) =>
          userCellRenderer({ value: params.data.modifiedBy }),
        cellStyle: { "border-right-color": "#e2e2e2" },
        autoHeight: true,
        wrapText: true,
      },
      {
        flex: 0.6,
        headerName: "Details",
        field: "id",
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <IconButton
              aria-label="edit"
              onClick={() => {
                try {
                  dispatch(
                    saveGridState({
                      filterModel: gridRef.current.api.getFilterModel(),
                      sortModel: gridRef.current.api.getSortModel(),
                      page: gridRef.current.api.paginationGetCurrentPage(),
                    })
                  );
                  dispatch(
                    setDateRange({
                      fromDate: dateValue?.toISOString(),
                      toDate: toDateValue?.toISOString(),
                    })
                  );
                } catch (e) {}

                navigate(
                  PATH_SALES_ORDERS_DETAIL_WITHOUT_PARAM + params.data.id,
                  {
                    state: {
                      date: dayjs(dateValue).format("YYYY-MM-DD"),
                      isEdit: true,
                    },
                  }
                );
              }}
            >
              <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
            </IconButton>
            <IconButton
              aria-label="visibility"
              onClick={() => {
                try {
                  dispatch(
                    saveGridState({
                      filterModel: gridRef.current.api.getFilterModel(),
                      sortModel: gridRef.current.api.getSortModel(),
                      page: gridRef.current.api.paginationGetCurrentPage(),
                    })
                  );
                  dispatch(
                    setDateRange({
                      fromDate: dateValue?.toISOString(),
                      toDate: toDateValue?.toISOString(),
                    })
                  );
                } catch (e) {}
                navigate(
                  PATH_SALES_ORDERS_DETAIL_WITHOUT_PARAM + params.data.id,
                  {
                    state: {
                      date: dayjs(dateValue).format("YYYY-MM-DD"),
                      isEdit: false,
                    },
                  }
                );
              }}
            >
              <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
            </IconButton>
          </div>
        ),
      },
    ],
    [dateValue, toDateValue, dispatch, navigate]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      editable: false,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: { caseSensitive: false, defaultOption: "startsWith" },
      floatingFilter: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    }),
    []
  );

  const fetchOrders = useCallback((d1, d2) => {
    return APISALES.SalesOrders(
      d1.format("YYYY-MM-DD"),
      d2.format("YYYY-MM-DD")
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        return resp.response.sort((a, b) => b.id - a.id);
      });
  }, []);

  const getCacheKey = (fromDate, toDate) =>
    `${fromDate.format("YYYY-MM-DD")}_${toDate.format("YYYY-MM-DD")}`;

  const loadSalesOrders = async (from, to) => {
    try {
      setIsLoading(true);
      dispatch(
        setDateRange({
          fromDate: from?.toISOString(),
          toDate: to?.toISOString(),
        })
      );
    } catch (e) {}

    const cacheKey = getCacheKey(from, to);

    let data = null;

    if (ordersCache?.[cacheKey]) {
      data = ordersCache[cacheKey];
    } else {
      data = await fetchOrders(from, to);

      dispatch(saveOrdersToCache({ key: cacheKey, data }));
    }

    try {
      gridRef.current.api.setRowData(data);

      if (sortModel) gridRef.current.api.setSortModel(sortModel);
      if (filterModel) gridRef.current.api.setFilterModel(filterModel);
      gridRef.current.api.onFilterChanged?.();
      if (typeof page === "number") {
        gridRef.current.api.paginationGoToPage(page);
      }
    } catch (e) {}

    try {
      dispatch(
        saveGridState({
          filterModel: gridRef.current.api.getFilterModel(),
          sortModel: gridRef.current.api.getSortModel(),
          page: gridRef.current.api.paginationGetCurrentPage(),
        })
      );
    } catch (e) {}
    finally {
    setIsLoading(false); 
  }
  };

  const debouncedLoadSalesOrders = useDebounce(loadSalesOrders, 300);

  const onGridReady = useCallback(
    async (params) => {
      const d1 = date
        ? dayjs(date)
        : fromDate
        ? dayjs(fromDate)
        : dayjs().startOf("month");
      const d2 = date
        ? dayjs().endOf("month")
        : toDate
        ? dayjs(toDate)
        : dayjs().endOf("month");

      setDateValue(d1);
      setToDateValue(d2);

      APIMASTER.SiteCategories()
        .then((r) => r.json())
        .then((r) => r.statusCode === 200 && setSiteCategories(r.response));
      APIMASTER.ItemCategories()
        .then((r) => r.json())
        .then((r) => r.statusCode === 200 && setItemCategories(r.response));
      APIMASTER.SiteOptions()
        .then((r) => r.json())
        .then((r) => r.statusCode === 200 && setSites(r.response));
      APIMASTER.UomOptions()
        .then((r) => r.json())
        .then((r) => r.statusCode === 200 && setUoms(r.response));
      APIMASTER.UserOptions()
        .then((r) => r.json())
        .then((r) => r.statusCode === 200 && setUsers(r.response));
      APIMASTER.Items()
        .then((r) => r.json())
        .then((r) => {
          if (r.statusCode !== 200) return;
          const itemOptions = r.response.map((e) => ({
            name: e.itemName,
            value: e.id,
            dependentvalue: e.categoryId,
          }));
          setItems(itemOptions);
          setItemList(r.response);
        });

      const data = await fetchOrders(d1, d2);
      params.api.setRowData(data);

      try {
        if (sortModel) params.api.setSortModel(sortModel);
        if (filterModel) params.api.setFilterModel(filterModel);
        params.api.onFilterChanged?.();
        if (typeof page === "number") params.api.paginationGoToPage(page);
      } catch (e) {}
    },
    [date, fromDate, toDate, fetchOrders, filterModel, sortModel, page]
  );

  const onFilterChanged = (event) => {
    try {
      const model = event.api.getFilterModel();
      dispatch(
        saveGridState({
          filterModel: model,
          sortModel: gridRef.current?.api?.getSortModel?.() || null,
          page: gridRef.current?.api?.paginationGetCurrentPage?.() ?? 0,
        })
      );
    } catch (e) {}
  };

  return (
    <SideNav
      currentComponent={CUSTOMER_MANAGEMENT}
      subComponent={SALES_ORDERS}
      subComponentPath={PATH_SALES_ORDERS}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            navigate(PATH_SALES_ORDERS_DETAIL_WITHOUT_PARAM + 0, {
              state: { date: dayjs(dateValue).format("YYYY-MM-DD") },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
          onClick={() => {
            if (selectedRowId <= 0) {
              setAlertTitleText("Oops...", `Please select a row...`);
              return;
            }
            Swal.fire({
              title: "Change Status",
              input: "select",
              inputOptions: { P: "Pending", C: "Closed", SC: "Short Closed" },
              inputPlaceholder: "Select",
              confirmButtonText: "Save",
              showCancelButton: true,
            }).then((result) => {
              if (!result.isConfirmed) return;
              APISALES.SalesOrderChangeStatus(selectedRowId, {
                status: result.value,
              })
                .then((x) => x.json())
                .then((x) => {
                  if (x.statusCode === 200) {
                    setAlertTitleText("Status Changed", "");
                    fetchOrders(dateValue, toDateValue).then((data) => {
                      try {
                        const rowData = [];
                        gridRef.current.api.forEachNode((n) =>
                          rowData.push(n.data)
                        );
                        gridRef.current.api.applyTransaction({
                          remove: rowData,
                        });
                      } catch {}
                      try {
                        gridRef.current.api.applyTransaction({ add: data });
                      } catch {}
                    });
                  } else {
                    setAlertTitleText("Oops...", `Something went wrong...`);
                  }
                });
            });
          }}
        >
          {CHANGE_STATUS}
        </Button>
      </div>

      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(newValue) => setDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(newValue) => setToDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div
          align="right"
          style={{
            width: "100%",
            right: 0,
          }}
        >
          <Button
            disabled={isLoading}
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
              opacity: isLoading ? 0.6 : 1,
            }}
            onClick={() => {
              setIsLoading(true);
              debouncedLoadSalesOrders(dateValue, toDateValue);
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            onFilterChanged={onFilterChanged}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(params) => params.data.id}
            onSelectionChanged={onRowSelectionChanged}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}
